/** Max LCP image URLs returned by prewarm (matches collectLandingImageUrls). */
export const MAX_WARM_IMAGE_URLS = 3;

const PREWARM_PATH = "/events/prewarm";

type PrewarmResponse = { images?: string[] };

/**
 * Fetches the prewarm endpoint and returns image URLs for client cache warming.
 * Best-effort: returns an empty array on failure.
 */
export async function fetchPrewarmImageUrls(
  fetchFn: typeof fetch = fetch,
): Promise<string[]> {
  try {
    const res = await fetchFn(PREWARM_PATH);
    if (!res.ok) {
      return [];
    }
    const body = (await res.json()) as PrewarmResponse;
    const images = body.images ?? [];
    return images.slice(0, MAX_WARM_IMAGE_URLS);
  } catch {
    return [];
  }
}

/**
 * Warms the browser HTTP cache for landing images via fetch (no link rel=preload).
 */
export async function warmBrowserImageCache(
  urls: string[],
  signal?: AbortSignal,
  fetchFn: typeof fetch = fetch,
): Promise<void> {
  if (urls.length === 0) {
    return;
  }

  const capped = urls.slice(0, MAX_WARM_IMAGE_URLS);

  await Promise.allSettled(
    capped.map((url) =>
      fetchFn(url, {
        credentials: "same-origin",
        signal,
      }),
    ),
  );
}
